import type { AssetType } from "@/contracts";

/** What an asset can be judged on, as far as the form is concerned. */
export type Monitored = "TEMPERATURE" | "HUMIDITY" | "DISTANCE" | "CURRENT" | "OCCUPANCY";

/** The fields an example value can be offered for. */
export type RangeField =
  | "targetTempMinC"
  | "targetTempMaxC"
  | "targetHumidityMin"
  | "targetHumidityMax"
  | "targetDistanceMinMm"
  | "targetDistanceMaxMm"
  | "targetCurrentMinA"
  | "targetCurrentMaxA";

interface Monitoring {
  readonly monitors: readonly Monitored[];
  readonly examples: Readonly<Partial<Record<RangeField, string>>>;
}

/**
 * Which questions each kind of asset is worth asking, and an example answer.
 *
 * The asset row carries every range at once and the API accepts any
 * combination, because the engine picks the range to judge by the *device's*
 * type rather than the asset's — a cold room and a tank can sit in the same
 * column layout. That made the form ask a meeting room for its minimum
 * temperature and a tank for its humidity, which are questions nobody can
 * answer, so this table decides what is actually asked.
 *
 * The consequence is deliberate and worth knowing: a combination this table
 * does not list cannot be entered through the form. A tank that also has a
 * temperature probe has to be registered as `OTHER`, which is the one type
 * that asks everything.
 *
 * **The example values are the seed's own demo ranges, not a standard and not
 * a recommendation.** They are placeholders and are never written into the
 * field: an asset with no range set is deliberately not judged against one
 * (ADR-045), and a number that arrived by itself is exactly the kind of
 * threshold nobody would notice was never chosen. Where the project has no
 * figure of its own — humidity anywhere, temperature in a production area —
 * the field is left without an example rather than given an invented one.
 */
const MONITORING: Record<AssetType, Monitoring> = {
  REFRIGERATOR: {
    monitors: ["TEMPERATURE", "HUMIDITY"],
    examples: { targetTempMinC: "2", targetTempMaxC: "8" },
  },
  FREEZER: {
    monitors: ["TEMPERATURE", "HUMIDITY"],
    examples: { targetTempMinC: "-22", targetTempMaxC: "-18" },
  },
  COLD_ROOM: {
    monitors: ["TEMPERATURE", "HUMIDITY"],
    examples: { targetTempMinC: "-25", targetTempMaxC: "-20" },
  },
  DISPLAY_CASE: {
    monitors: ["TEMPERATURE", "HUMIDITY"],
    examples: { targetTempMinC: "-20", targetTempMaxC: "-16" },
  },
  TRANSPORT_UNIT: {
    monitors: ["TEMPERATURE", "HUMIDITY"],
    examples: { targetTempMinC: "-20", targetTempMaxC: "-14" },
  },
  // A stockroom is watched for what is in it and for who walks into it, which
  // is the pair the seed's locked stockroom demonstrates.
  STORAGE_ROOM: { monitors: ["TEMPERATURE", "HUMIDITY", "OCCUPANCY"], examples: {} },
  SERVER_ROOM: {
    monitors: ["TEMPERATURE", "HUMIDITY", "OCCUPANCY"],
    examples: { targetTempMinC: "18", targetTempMaxC: "27" },
  },
  PRODUCTION_AREA: { monitors: ["TEMPERATURE", "HUMIDITY", "OCCUPANCY"], examples: {} },
  TANK: {
    monitors: ["DISTANCE"],
    examples: { targetDistanceMinMm: "600", targetDistanceMaxMm: "2800" },
  },
  MEETING_ROOM: { monitors: ["OCCUPANCY"], examples: {} },
  // One conductor. No example band: a number that arrived by itself would
  // be a threshold nobody chose (ADR-102, ADR-045).
  CIRCUIT: { monitors: ["CURRENT"], examples: {} },
  // The escape hatch, and the only type that asks everything.
  OTHER: { monitors: ["TEMPERATURE", "HUMIDITY", "DISTANCE", "CURRENT", "OCCUPANCY"], examples: {} },
};

/** Whether this kind of asset is ever judged on that measurement. */
export function monitors(type: AssetType, what: Monitored): boolean {
  return MONITORING[type].monitors.includes(what);
}

/** An example bound for the field, or nothing where the project has no figure. */
export function exampleFor(type: AssetType, field: RangeField): string | undefined {
  return MONITORING[type].examples[field];
}
